import { useEffect, useState } from 'react';
import { api } from './api';

/**
 * Compare shortlist store.
 *
 * Guest + free shortlists are ephemeral by design: they live in
 * sessionStorage so they survive navigation and a reload within the session
 * (and carry through login, since the tab is the same), but are never synced
 * across devices. Saving a comparison permanently is the paid feature and
 * goes through /api/compare/saved instead.
 */
const KEY = 'indusfact_compare_v1';

const read = () => {
  try {
    const raw = sessionStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
};

let items = read();
const subs = new Set();

const persist = () => {
  try { sessionStorage.setItem(KEY, JSON.stringify(items)); } catch { /* quota / private mode */ }
  subs.forEach((fn) => fn(items));
};

export const compareMeta = (listing) => ({
  id: listing.id,
  title: listing.title,
  listing_type: listing.listing_type,
  thumb_url: (listing.media || []).find((m) => (m.kind || 'image') === 'image')?.thumb_url
    || (listing.media || []).find((m) => (m.kind || 'image') === 'image')?.public_url
    || null,
});

export const getCompareItems = () => items;
export const inCompare = (id) => items.some((i) => i.id === id);

/** Add a listing. Returns { ok, reason } — 'limit' | 'type_mismatch'. */
export const addToCompare = (listing, maxColumns) => {
  if (inCompare(listing.id)) return { ok: true };
  const type = items[0]?.listing_type;
  if (type && listing.listing_type !== type) return { ok: false, reason: 'type_mismatch', existingType: type };
  if (items.length >= maxColumns) return { ok: false, reason: 'limit', limit: maxColumns };
  items = [...items, compareMeta(listing)];
  persist();
  return { ok: true };
};

export const removeFromCompare = (id) => {
  items = items.filter((i) => i.id !== id);
  persist();
};

export const clearCompare = () => {
  items = [];
  persist();
};

export const setCompareItems = (next) => {
  items = next.filter(Boolean);
  persist();
};

// ── Viewer-aware limits (admin-adjustable, so always read from the API) ──
let configCache = { key: null, promise: null };

const configKey = () => (localStorage.getItem('indusfact_token') ? 'auth' : 'guest');

export const getCompareConfig = () => {
  const key = configKey();
  if (configCache.key !== key || !configCache.promise) {
    configCache = {
      key,
      promise: api.get('/compare/config')
        .then((r) => r.data)
        .catch(() => ({ max_columns: 2, guest_max_columns: 2, user_max_columns: 4, is_logged_in: false, can_save: false })),
    };
  }
  return configCache.promise;
};

export const useCompare = () => {
  const [list, setList] = useState(items);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    const fn = (next) => setList(next);
    subs.add(fn);
    setList(items);
    return () => subs.delete(fn);
  }, []);

  useEffect(() => {
    let alive = true;
    getCompareConfig().then((c) => { if (alive) setConfig(c); });
    return () => { alive = false; };
  }, []);

  const maxColumns = config?.max_columns ?? 2;
  return {
    items: list,
    count: list.length,
    config,
    maxColumns,
    guestMax: config?.guest_max_columns ?? 2,
    userMax: config?.user_max_columns ?? 4,
    isLoggedIn: !!config?.is_logged_in,
    canSave: !!config?.can_save,
    listingType: list[0]?.listing_type || null,
    has: (id) => list.some((i) => i.id === id),
    add: (listing) => addToCompare(listing, maxColumns),
    remove: removeFromCompare,
    clear: clearCompare,
  };
};
